import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Gift, Users, DollarSign, Trophy, Copy as CopyIcon,
  Share2, Twitter, Loader2, ArrowRight, CheckCircle2,
} from "lucide-react";

const F = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));

// Status pill shown next to each invitee row
function StatusPill({ status }) {
  const map = {
    rewarded: { label: "Rewarded", cls: "bg-[hsl(var(--gain))]/15 text-[hsl(var(--gain))] border-[hsl(var(--gain))]/30" },
    verified_publisher: { label: "Verified publisher", cls: "bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))] border-[hsl(var(--ai))]/30" },
    signed_up: { label: "Signed up", cls: "bg-secondary text-muted-foreground border-border" },
  };
  const m = map[status] || map.signed_up;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
  );
}

export default function Referrals() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/referrals/me").then((r) => r.data),
      api.get("/referrals/leaderboard?limit=10").then((r) => r.data.leaderboard || []).catch(() => []),
    ])
      .then(([me, lb]) => {
        if (cancelled) return;
        setData(me);
        setBoard(lb);
      })
      .catch(() => toast.error("Couldn't load referral stats"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const inviteUrl = useMemo(() => {
    if (!data?.code) return "";
    return `${window.location.origin}/register?ref=${data.code}`;
  }, [data?.code]);

  const shareText = useMemo(() => {
    return `I'm using Aanar to paper-trade stocks & crypto with AI bots. Grab $100k in virtual cash and try it — ${inviteUrl}`;
  }, [inviteUrl]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Clipboard blocked — long-press the field to copy"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Aanar — paper trade with AI", text: shareText, url: inviteUrl });
      } catch { /* user cancelled */ }
    } else {
      copyUrl();
    }
  };

  const tweet = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="referrals-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading referrals…
      </div>
    );
  }

  const counts = data?.counts || { signed_up: 0, verified_publisher: 0, rewarded: 0 };
  const totalInvited = counts.signed_up + counts.verified_publisher + counts.rewarded;
  const rewardPer = data?.reward_per_verification || 10;

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Rewards</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
          <Gift className="w-7 h-7" /> Refer & earn
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Share your invite link. When someone you invited publishes a bot that reaches Bronze tier,
          you earn <span className="text-foreground font-medium">${F(rewardPer, 0)}</span> — credited straight
          to your publisher balance and payable to your bank via Stripe.
        </p>
      </div>

      {/* Invite link + share row */}
      <Card className="p-5 rounded-2xl border-2 border-[hsl(var(--ai))]/30 bg-gradient-to-br from-[hsl(var(--ai))]/5 to-transparent" data-testid="referral-invite-card">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Your invite link</div>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Input
            readOnly
            value={inviteUrl}
            data-testid="referral-invite-input"
            onFocus={(e) => e.target.select()}
            className="font-mono-tab text-xs sm:text-sm bg-background/60"
          />
          <div className="flex gap-2">
            <Button onClick={copyUrl} data-testid="referral-copy-btn" className="rounded-full flex-1 sm:flex-initial">
              {copied ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <CopyIcon className="w-4 h-4 mr-2" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button onClick={nativeShare} variant="outline" data-testid="referral-share-btn" className="rounded-full">
              <Share2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            <Button onClick={tweet} variant="outline" data-testid="referral-tweet-btn" className="rounded-full">
              <Twitter className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Your code: <span className="font-mono-tab text-foreground" data-testid="referral-code">{data?.code}</span>
        </div>
      </Card>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 rounded-2xl" data-testid="kpi-invited">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Users className="w-3 h-3" /> Invited
          </div>
          <div className="text-2xl md:text-3xl font-mono-tab mt-2">{totalInvited}</div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="kpi-publishers">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Became publishers</div>
          <div className="text-2xl md:text-3xl font-mono-tab mt-2">
            {counts.verified_publisher + counts.rewarded}
          </div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="kpi-rewarded">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rewarded</div>
          <div className="text-2xl md:text-3xl font-mono-tab mt-2">{counts.rewarded}</div>
        </Card>
        <Card className="p-4 rounded-2xl border-2 border-[hsl(var(--gain))]/30" data-testid="kpi-earned">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Earned
          </div>
          <div className="text-2xl md:text-3xl font-mono-tab gain mt-2">${F(data?.total_earned)}</div>
        </Card>
      </div>

      {/* Funnel explainer */}
      <Card className="p-5 rounded-2xl" data-testid="referral-funnel">
        <div className="text-lg font-display font-semibold mb-3">How the reward flows</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="p-3 rounded-xl bg-secondary/50" data-testid="funnel-step-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Step 1</div>
            <div className="font-medium mt-1">They sign up with your link</div>
            <div className="text-xs text-muted-foreground mt-1">Tracked automatically — nothing for you to submit.</div>
          </div>
          <div className="p-3 rounded-xl bg-secondary/50" data-testid="funnel-step-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Step 2</div>
            <div className="font-medium mt-1">They publish a bot to Bronze tier</div>
            <div className="text-xs text-muted-foreground mt-1">Bronze = 30 verified trades with positive edge.</div>
          </div>
          <div className="p-3 rounded-xl bg-[hsl(var(--gain))]/10 border border-[hsl(var(--gain))]/30" data-testid="funnel-step-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Step 3</div>
            <div className="font-medium mt-1">You earn ${F(rewardPer, 0)}</div>
            <div className="text-xs text-muted-foreground mt-1">Credited to your publisher balance. Withdraw anytime.</div>
          </div>
        </div>
      </Card>

      {/* Recent invitees */}
      <Card className="p-5 rounded-2xl" data-testid="referral-invitees">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-display font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> Recent invitees
          </div>
          <Button variant="link" size="sm" className="text-xs text-muted-foreground" onClick={() => window.location.assign("/earnings")}>
            View earnings <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        {(data?.referrals || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="referral-empty">
            No invitees yet. Share your link above — every friend who publishes a Bronze-tier bot pays you ${F(rewardPer, 0)}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 font-medium">Invitee</th>
                  <th className="text-left py-2 font-medium">Joined</th>
                  <th className="text-left py-2 font-medium">Status</th>
                  <th className="text-right py-2 font-medium">Reward</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.map((r, i) => {
                  const email = r.referred_email || r.invitee_email;
                  const initial = ((email || "?").trim()[0] || "?").toUpperCase();
                  const masked = email
                    ? email.replace(/(.)(.*)(@.*)/, (_, a, b, c) => `${a}${"•".repeat(Math.min(b.length, 4))}${c}`)
                    : "—";
                  return (
                    <tr key={i} className="border-b border-border/60" data-testid={`referral-row-${i}`}>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">{initial}</div>
                          <span className="font-mono-tab text-xs">{masked}</span>
                        </div>
                      </td>
                      <td className="py-2 text-muted-foreground text-xs">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2"><StatusPill status={r.status} /></td>
                      <td className="py-2 text-right font-mono-tab">
                        {r.status === "rewarded"
                          ? <span className="gain">+${F(r.reward_amount_usd || rewardPer)}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Public leaderboard */}
      <Card className="p-5 rounded-2xl" data-testid="referral-leaderboard-card">
        <div className="text-lg font-display font-semibold mb-3 flex items-center gap-2">
          <Trophy className="w-4 h-4" /> Top referrers this month
        </div>
        {board.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="leaderboard-empty">
            Be the first — the leaderboard resets when it fills up.
          </div>
        ) : (
          <div className="space-y-2">
            {board.map((row, i) => (
              <div key={i} data-testid={`leaderboard-row-${i}`}
                   className={`flex items-center justify-between p-2.5 rounded-xl ${
                     i === 0 ? "bg-[hsl(var(--ai))]/10 border border-[hsl(var(--ai))]/30"
                     : i < 3 ? "bg-secondary/60" : "bg-secondary/30"
                   }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono-tab font-bold ${
                    i === 0 ? "bg-[hsl(var(--ai))] text-background"
                    : i === 1 ? "bg-foreground text-background"
                    : i === 2 ? "bg-muted-foreground text-background"
                    : "bg-secondary text-muted-foreground"
                  }`}>{i + 1}</div>
                  <div className="w-7 h-7 rounded-full bg-background border border-border flex items-center justify-center text-xs font-medium">
                    {row.initial}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{row.verified_referrals} verified referral{row.verified_referrals === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <div className="font-mono-tab gain">${F(row.earned_usd)}</div>
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground text-center mt-3">
          Names anonymized to protect privacy. Only your own row shows your real details.
        </div>
      </Card>
    </div>
  );
}
